import React from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useResponsiveStyles } from '../../hooks/useResponsiveStyles';
import theme from '../../theme';

interface ResponsiveTableProps {
  data: any[];
  columns: {
    key: string;
    title: string;
    width?: number;
    render?: (value: any, item: any) => React.ReactNode;
  }[];
  onRowPress?: (item: any) => void;
  loading?: boolean;
}

export const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
  data,
  columns,
  onRowPress,
  loading = false,
}) => {
  const responsive = useResponsiveStyles();

  if (responsive.isMobile) {
    // Mobile: Card-based layout
    return (
      <View style={styles.mobileContainer}>
        {data.map((item, index) => (
          <TouchableOpacity
            key={index}
            style={styles.mobileCard}
            onPress={() => onRowPress?.(item)}
            disabled={!onRowPress}
          >
            {columns.map((column) => (
              <View key={column.key} style={styles.mobileRow}>
                <Text style={styles.mobileLabel}>{column.title}:</Text>
                <View style={styles.mobileValue}>
                  {column.render ? 
                    column.render(item[column.key], item) : 
                    <Text style={styles.mobileValueText}>{item[column.key]}</Text>
                  }
                </View>
              </View>
            ))}
            {onRowPress && (
              <View style={styles.mobileArrow}>
                <Ionicons name="chevron-forward" size={16} color={theme.colors.gray400} />
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>
    );
  }

  // Web: Traditional table layout
  return (
    <View style={styles.webContainer}>
      {/* Header */}
      <View style={styles.webHeader}>
        {columns.map((column) => (
          <Text
            key={column.key}
            style={[
              styles.webHeaderText,
              { width: column.width || 'auto', flex: column.width ? undefined : 1 }
            ]}
          >
            {column.title}
          </Text>
        ))}
      </View>

      {/* Rows */}
      {data.map((item, index) => (
        <TouchableOpacity
          key={index}
          style={styles.webRow}
          onPress={() => onRowPress?.(item)}
          disabled={!onRowPress}
        >
          {columns.map((column) => (
            <View
              key={column.key}
              style={[
                styles.webCell,
                { width: column.width || 'auto', flex: column.width ? undefined : 1 }
              ]}
            >
              {column.render ? 
                column.render(item[column.key], item) : 
                <Text style={styles.webCellText}>{item[column.key]}</Text>
              }
            </View>
          ))}
        </TouchableOpacity>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  // Mobile styles
  mobileContainer: {
    padding: theme.spacing.sm,
  },
  mobileCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    ...theme.shadows.sm,
  },
  mobileRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  mobileLabel: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textSecondary,
    width: '40%',
  },
  mobileValue: {
    width: '60%',
    alignItems: 'center',
  },
  mobileValueText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  mobileArrow: {
    position: 'absolute',
    right: theme.spacing.sm,
    top: '50%',
    transform: [{ translateY: -8 }],
  },

  // Web styles
  webContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    ...theme.shadows.sm,
  },
  webHeader: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray50,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  webHeaderText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.semibold as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  webRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  webCell: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  webCellText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
});
